// src/components/GenericForm.js

import React from "react";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Radio,
  Checkbox,
  DatePicker,
  Button,
  Upload,
  Row,
  Col,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Option } = Select;

const GenericForm = ({
  fields,
  onFinish,
  onFinishFailed,
  initialValues = {},
  layout = "vertical",
  submitButtonText = "Submit",
  hideSubmitButton = false,
  submitButtonProps = {},
}) => {
  // Render individual form items based on field configuration
  const renderFormItem = (field) => {
    const {
      label,
      name,
      type,
      placeholder,
      rules,
      options,
      component,
      props,
      max,
      colSpan = 24,
      hidden,
      onChange,
      initialValue,
    } = field;

    // Skip rendering if field is hidden
    if (hidden) {
      return null;
    }

    let inputComponent;

    switch (type) {
      case "input":
        inputComponent = (
          <Input autoComplete="off" placeholder={placeholder} {...props} />
        );
        break;
      case "password":
        inputComponent = (
          <Input.Password autoComplete="off" placeholder={placeholder} {...props} />
        );
        break;
      case "textarea":
        inputComponent = <TextArea autoComplete="off" placeholder={placeholder} {...props} />;
        break;
      case "upload":
        inputComponent = (
          <Upload
            beforeUpload={() => false}
            listType={props?.listType || "picture-card"}
            multiple={props?.multiple}
            accept={props?.accept}
            maxCount={props?.maxCount}
            {...props}
          >
            {props?.children || (
              <Button icon={<UploadOutlined />}>Click to Upload</Button>
            )}
          </Upload>
        );
        break;
      case "number":
        inputComponent = (
          <InputNumber
            autoComplete="off"
            max={props?.max}
            placeholder={placeholder}
            style={{ width: "100%" }}
            {...props}
          />
        );
        break;
      case "select":
        inputComponent = (
          <Select
            autoComplete="off"
            placeholder={placeholder}
            onChange={onChange}
            showSearch={
              props && props.showSearch !== undefined ? props.showSearch : true
            } // Default to true
            optionFilterProp={
              props && props.optionFilterProp
                ? props.optionFilterProp
                : "children"
            }
            filterOption={
              props && props.filterOption
                ? props.filterOption
                : (input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
            }
            {...props}
          >
            {options &&
              options.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
          </Select>
        );
        break;
      case "radio":
        inputComponent = (
          <Radio.Group {...props}>
            {options &&
              options.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
          </Radio.Group>
        );
        break;
      case "checkbox":
        inputComponent = <Checkbox.Group options={options} {...props} />;
        break;
      case "date":
        inputComponent = <DatePicker autoComplete="off" style={{ width: "100%" }} {...props} />;
        break;
      case "custom":
        inputComponent = component;
        break;
      default:
        inputComponent = <Input autoComplete="off" placeholder={placeholder} {...props} />;
    }

    const formItemProps = {
      key: name,
      label: label,
      name: name,
      rules: rules,
      initialValue: initialValue,
    };

    // Handle different field types with appropriate props
    if (type === "checkbox") {
      formItemProps.valuePropName = "checked";
    } else if (type === "upload") {
      formItemProps.valuePropName = "fileList";
      formItemProps.getValueFromEvent = (e) => {
        if (Array.isArray(e)) {
          return e;
        }
        return e?.fileList || [];
      };
    } else {
      formItemProps.valuePropName = "value";
    }

    return (
      <Col xs={24} sm={colSpan} md={colSpan} lg={colSpan} xl={colSpan} key={name}>
        <Form.Item {...formItemProps}>
          {inputComponent}
        </Form.Item>
      </Col>
    );
  };

  return (
    <Form
      layout={layout}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={initialValues}
      autoComplete="off"
    >
      <Row gutter={[16, 0]}>
        {fields.map((field) => renderFormItem(field))}
      </Row>

      {!hideSubmitButton && (
        <Form.Item style={{ marginTop: 16 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={submitButtonProps.loading}
            disabled={submitButtonProps.disabled}
            style={submitButtonProps.style}
            icon={submitButtonProps.icon}
          >
            {submitButtonProps.loading
              ? submitButtonProps.loadingText || "Submitting..."
              : submitButtonText}
          </Button>
        </Form.Item>
      )}
    </Form>
  );
};

export default GenericForm;
